import moment from "moment";
import { types, flow } from "mobx-state-tree";
import * as TwitterAPI from "api/twitter";

import CONSTANTS from "const/constants";
import * as Analytics from "page/analytics";
import JSONVideoCategories from "json/socialvideocategories.min.json";

import { UserCredentialBaseStore } from "./user-credential-base-store";

const {
    CREDENTIAL_TYPE_LABELS,
    PLATFORM_TYPES,
    TWITTER_MONETIZATION,
    TWITTER_WHITELIST_LIMIT,
    TWITTER_NON_WHITELIST_LIMIT
} = CONSTANTS;

export const proMediaApiFeatures = {
    MEDIATOOL_MONETIZATION_USER: `mediatool_monetization_user`,
    MEDIATOOL_AMPLIFY_DIRECT: `mediatool_amplify_direct`,
    MEDIATOOL_UPLOAD_LONG: `mediatool_upload_long`
};

const model = {
    twitterUsername: types.maybeNull(types.string),
    twitterProfileImageUrl: types.maybeNull(types.string),
    twitterName: types.maybeNull(types.string),
    twitterProMediaApiFeatures: types.optional(types.array(types.string), [])
};

const volatile = (self) => {
    const __name__ = self.name;
    const __profileImage__ = self.profileImage;

    return {
        name: self?.twitterUsername ?? __name__,
        profileImage: self?.twitterProfileImageUrl ?? __profileImage__,
        typeLabel: CREDENTIAL_TYPE_LABELS.TWITTER,
        platformType: PLATFORM_TYPES.TWITTER,
        shareKey: `shareViaTwitterCount`,
        monetizationEnabled: self.isMonetizationEnabled(),
        twitterAdsEnabled: false,
        monetizationActive: false,
        twitterMonetizationType: TWITTER_MONETIZATION.NONE,
        twitterVideoCategoryIds: ``,
        twitterBlacklistVideoCategoryIds: ``,
        twitterBlacklistAdvertiserIds: ``,
        twitterInStreamAdsCountryIds: ``,
        twitterSponsorshipProgramExpirationDate: undefined,
        twitterSponsorshipProgramId: ``,
        twitterCardTitle: ``,
        twitterCardDestinationUrl: ``,
        twitterAdAccountId: ``
    };
};

const views = (self) => {
    return {
        isProMediaEnabled() {
            // Does the user have access to the Pro Media API?
            return !!self?.twitterProMediaApiFeatures?.length;
        },

        getProMediaApiFeatures() {
            if (self.isProMediaEnabled()) {
                return self.twitterProMediaApiFeatures;
            }
            return [];
        },

        isMonetizationEnabled() {
            return (
                self.areVideoAdsEnabled() ||
                self.areSponsorshipsEnabled() ||
                self.areTwitterCardsEnabled()
            );
        },

        areVideoAdsEnabled() {
            if (!self.isProMediaEnabled()) {
                return false;
            }

            const features = self.twitterProMediaApiFeatures;
            return (
                features?.includes?.(
                    proMediaApiFeatures.MEDIATOOL_MONETIZATION_USER
                ) ?? false
            );
        },

        areTwitterCardsEnabled() {
            return self.twitterAdsEnabled;
        },

        areSponsorshipsEnabled() {
            if (!self.isProMediaEnabled()) {
                return false;
            }

            const features = self.twitterProMediaApiFeatures;
            return (
                features?.includes?.(
                    proMediaApiFeatures.MEDIATOOL_AMPLIFY_DIRECT
                ) ?? false
            );
        }
    };
};

const viewOverrides = (self) => {
    const __getDisplayName__ = self.getDisplayName;

    return {
        get displayName() {
            return `@${__getDisplayName__()}`;
        },

        isSocialMedia() {
            return true;
        }
    };
};

const actions = (self) => {
    const __onShareSuccessful__ = self.onShareSuccessful;

    return {
        afterCreate() {
            if (self.isProMediaEnabled()) {
                self.loadPersistedSettings();
            }
        },

        onShareSuccessful(response) {
            return __onShareSuccessful__.call(self, self.shareKey, response);
        },

        onSponsorshipsFetchSuccess(res) {
            self.sponsorshipPrograms = res.sponsorshipPrograms;
            // need to trigger that manually even if no change occurred
            self?.__model?.trigger?.(`sponsorshipPrograms:change`);
        },

        getMonetizationShareInfo() {
            if (
                !self.monetizationActive ||
                !self.twitterMonetizationType ||
                self.twitterMonetizationType === TWITTER_MONETIZATION.NONE
            ) {
                return {};
            }

            let monetizationData;

            switch (self.twitterMonetizationType) {
                case TWITTER_MONETIZATION.VIDEO_ADS:
                    monetizationData = self.getVideoAdsSettings();
                    break;
                case TWITTER_MONETIZATION.SPONSORSHIPS:
                    monetizationData = self.getSponsorshipsSettings();
                    break;
                case TWITTER_MONETIZATION.CARDS:
                    monetizationData = self.getCardSettings();
                    break;
            }

            Analytics.trackTwitterMonetization(
                self.twitterMonetizationType,
                self.extractVideoCategoryNames(
                    monetizationData.twitterVideoCategoryIds
                )
            );

            return monetizationData;
        },

        extractVideoCategoryNames(categoryIds) {
            if (!categoryIds) {
                return;
            }
            const ids = categoryIds.split(`,`).map((str) => parseInt(str));
            const categories = JSONVideoCategories.twitter.categories;
            const filtered = categories.filter((cat) => {
                return ids.includes(cat.categoryId);
            });
            return filtered.map((cat) => cat.categoryDescription);
        },

        getVideoAdsSettings() {
            return {
                twitterVideoCategoryIds: self.twitterVideoCategoryIds,
                twitterBlacklistVideoCategoryIds:
                    self.twitterBlacklistVideoCategoryIds,
                twitterBlacklistAdvertiserIds:
                    self.twitterBlacklistAdvertiserIds,
                twitterInStreamAdsCountryIds: self.twitterInStreamAdsCountryIds
            };
        },

        getSponsorshipsSettings() {
            const expirationDate = self.twitterSponsorshipProgramExpirationDate;
            const twitterSponsorshipExpirationDate = moment(
                expirationDate,
                `h:mma, DD/MM/YYYY`
            ).isValid()
                ? moment(expirationDate, `h:mma, DD/MM/YYYY`).unix() * 1000
                : expirationDate * 1000;
            const twitterSponsorshipProgramExpirationDate =
                twitterSponsorshipExpirationDate &&
                twitterSponsorshipExpirationDate > 0
                    ? twitterSponsorshipExpirationDate
                    : null;
            const sponsorshipSettings = {
                twitterSponsorshipProgramId: self.twitterSponsorshipProgramId
            };

            if (twitterSponsorshipProgramExpirationDate) {
                sponsorshipSettings.twitterSponsorshipProgramExpirationDate =
                    twitterSponsorshipExpirationDate;
            }

            return sponsorshipSettings;
        },

        getCardSettings() {
            return {
                twitterCardTitle: self.twitterCardTitle,
                twitterCardDestinationUrl: self.twitterCardDestinationUrl,
                twitterAdAccountId: self.twitterAdAccountId
            };
        },

        getClipMaxDuration() {
            const features = self.getProMediaApiFeatures();
            const longVideoFeature = proMediaApiFeatures.MEDIATOOL_UPLOAD_LONG;
            if (features.includes(longVideoFeature)) {
                return TWITTER_WHITELIST_LIMIT;
            }

            return TWITTER_NON_WHITELIST_LIMIT;
        },

        setTwitterProMediaApiFeatures(features) {
            self.twitterProMediaApiFeatures = features;
        },

        setTwitterAdsEnabled(enabled) {
            self.twitterAdsEnabled = enabled;
        },

        setMonetizationActive(active) {
            self.monetizationActive = active;
        },

        setTwitterMonetizationType(type) {
            self.twitterMonetizationType = type;
        },

        setTwitterVideoCategoryIds(ids) {
            self.twitterVideoCategoryIds = ids;
        },

        setTwitterBlacklistVideoCategoryIds(ids) {
            self.twitterBlacklistVideoCategoryIds = ids;
        },

        setTwitterBlacklistAdvertiserIds(ids) {
            self.twitterBlacklistAdvertiserIds = ids;
        },

        setTwitterInStreamAdsCountryIds(ids) {
            self.twitterInStreamAdsCountryIds = ids;
        },

        setTwitterSponsorshipProgramExpirationDate(date) {
            self.twitterSponsorshipProgramExpirationDate = date;
        },

        setTwitterSponsorshipProgramId(id) {
            self.twitterSponsorshipProgramId = id;
        },

        setTwitterCardTitle(title) {
            self.twitterCardTitle = title;
        },

        setTwitterCardDestinationUrl(url) {
            self.twitterCardDestinationUrl = url;
        },

        setTwitterAdAccountId(id) {
            self.twitterAdAccountId = id;
        }
    };
};

const asyncActions = (self) => {
    const __share__ = self.share;

    return {
        /**
         * Overrides the base share method to add youtube specific share data
         * @async
         * @param {String} shareId
         * @param {object} data
         * @return {ShareObject | Error}
         */
        share: flow(function* share(shareId, data) {
            return yield __share__.call(self, shareId, {
                ...data,
                ...self.getMonetizationShareInfo()
            });
        }),

        fetchSponsorshipPrograms: flow(function* fetchSponsorshipPrograms() {
            try {
                const response = yield TwitterAPI.getSponsorshipPrograms();
                self.onSponsorshipsFetchSuccess(response);
            } catch (error) {
                console.error(
                    `Error fetching X (Twitter) Sponsorship Programs`,
                    error
                );
            }
        })
    };
};

const _UserCredentialTwitterStore = types
    .model(`UserCredentialTwitter`, model)
    .volatile(volatile)
    .views(views)
    .views(viewOverrides)
    .actions(actions)
    .actions(asyncActions);

export const UserCredentialTwitterStore = types
    .compose(UserCredentialBaseStore, _UserCredentialTwitterStore)
    .named(`UserCredentialTwitterStore`);
